/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OfferDetailTab } from 'types/enums/specific/OfferDetailTab'

interface UseOfferDetailTabsParams {
    hasLinkedOpportunity: boolean
    hasLinkedCompany: boolean
    hasLinkedBooking: boolean
    loadLead: () => void
    loadCompany: () => void
}

export const useOfferDetailTabs = ({
    hasLinkedOpportunity,
    hasLinkedCompany,
    hasLinkedBooking,
    loadLead,
    loadCompany,
}: UseOfferDetailTabsParams) => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState<OfferDetailTab>(OfferDetailTab.Detail)

    const tabs = [
        { id: OfferDetailTab.Detail, name: t('Offer') },
        {
            id: OfferDetailTab.Oppurtunity,
            name: t('Opportunity'),
            disabled: !hasLinkedOpportunity,
            ...(hasLinkedOpportunity
                ? {}
                : {
                      tooltipId: 'opportunity-disabled-tooltip',
                      tooltipContent: t('No opportunity linked to this offer'),
                  }),
        },
        {
            id: OfferDetailTab.Company,
            name: t('Account'),
            disabled: !hasLinkedCompany,
            ...(hasLinkedCompany
                ? {}
                : {
                      tooltipId: 'company-disabled-tooltip',
                      tooltipContent: t('No company linked to this offer'),
                  }),
        },
        { id: OfferDetailTab.EmailTemplate, name: t('Mail-Vorlage') },
        {
            id: OfferDetailTab.Booking,
            name: t('Reservation'),
            disabled: !hasLinkedBooking,
        },
    ]

    const handleTabClick = (tab: OfferDetailTab) => {
        switch (tab) {
            case OfferDetailTab.Oppurtunity:
                if (hasLinkedOpportunity) {
                    setActiveTab(tab)
                    loadLead()
                }
                break
            case OfferDetailTab.Company:
                if (hasLinkedCompany) {
                    setActiveTab(tab)
                    loadCompany()
                }
                break
            case OfferDetailTab.Detail:
            case OfferDetailTab.EmailTemplate:
            case OfferDetailTab.Booking:
                setActiveTab(tab)
                break
        }
    }

    return { activeTab, tabs, handleTabClick }
}
